import { Link } from 'react-router-dom'
import type { EAArtifactListItem } from '../../types/ea'
import EAVendorBadge from './EAVendorBadge'

interface EAArtifactCardProps {
  artifact: EAArtifactListItem
  projectId: string
}

const PHASE_COLORS: Record<string, string> = {
  requirements: '#3b82f6',
  design: '#8b5cf6',
  deployment: '#10b981',
  qc: '#f59e0b',
  deliverables: '#06b6d4',
}

const PHASE_LABELS: Record<string, string> = {
  requirements: 'Requirements',
  design: 'Design',
  deployment: 'Deployment',
  qc: 'QC',
  deliverables: 'Deliverables',
}

const QC_STATUS_COLORS: Record<string, string> = {
  pending_review: '#f59e0b',
  approved: '#10b981',
  approved_with_findings: '#f59e0b',
  rejected: '#ef4444',
}

const QC_STATUS_LABELS: Record<string, string> = {
  pending_review: 'Pending Review',
  approved: 'Approved',
  approved_with_findings: 'Approved w/ Findings',
  rejected: 'Rejected',
}

const FORMAT_ICONS: Record<string, string> = {
  md: '\u{1F4C4}',
  docx: '\u{1F4DD}',
  xlsx: '\u{1F4CA}',
  png: '\u{1F5BC}\uFE0F',
  drawio: '\u{1F5BC}\uFE0F',
  mmd: '\u{1F5BC}\uFE0F',
  cli: '\u2699\uFE0F',
  xml: '\u2699\uFE0F',
  tf: '\u2699\uFE0F',
  yml: '\u2699\uFE0F',
  json: '\u2699\uFE0F',
}

const ARTIFACT_TYPE_LABELS: Record<string, string> = {
  discovery_questionnaire: 'Discovery Questionnaire',
  hld: 'HLD',
  lld: 'LLD',
  bom: 'BOM',
  risk_register: 'Risk Register',
  diagram: 'Diagram',
  config: 'Config',
  script: 'Script',
  checklist: 'Checklist',
  runbook: 'Runbook',
  terraform: 'Terraform',
  ansible: 'Ansible',
  qc_report: 'QC Report',
  qc_checklist: 'QC Checklist',
  qc_findings: 'QC Findings',
  standards_compliance_check: 'Standards Check',
  integrated_architecture: 'Integrated Architecture',
  combined_bom: 'Combined BOM',
}

function formatFileSize(bytes?: number): string {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default function EAArtifactCard({ artifact, projectId }: EAArtifactCardProps) {
  const phaseColor = PHASE_COLORS[artifact.phase] || '#94a3b8'
  const qcColor = artifact.qc_status ? QC_STATUS_COLORS[artifact.qc_status] : undefined
  const qcLabel = artifact.qc_status ? QC_STATUS_LABELS[artifact.qc_status] : undefined
  const formatIcon = FORMAT_ICONS[artifact.file_format] || '\u{1F4C4}'

  return (
    <Link
      to={`/ea/projects/${projectId}/artifacts/${artifact.id}`}
      className="rounded-xl transition-all duration-300 hover:scale-[1.02] group cursor-pointer block"
      style={{
        background: 'rgba(30, 41, 59, 0.5)',
        border: '1px solid rgba(100, 180, 255, 0.15)',
      }}
    >
      <div className="p-5">
        {/* Top row: phase badge + version + promoted */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <span
              className="px-2 py-0.5 text-xs font-medium rounded-full"
              style={{
                background: `${phaseColor}22`,
                color: phaseColor,
                border: `1px solid ${phaseColor}44`,
              }}
            >
              {PHASE_LABELS[artifact.phase] || artifact.phase}
            </span>
            {artifact.vendor && <EAVendorBadge vendor={artifact.vendor} size="sm" />}
          </div>
          <div className="flex items-center space-x-2">
            <span
              className="px-2 py-0.5 text-xs font-medium rounded-md"
              style={{
                background: 'rgba(100, 116, 139, 0.15)',
                color: 'rgba(148, 163, 184, 0.7)',
                border: '1px solid rgba(100, 116, 139, 0.2)',
              }}
            >
              v{artifact.version}
            </span>
            {artifact.promoted_to_deliverables && (
              <span
                className="px-2 py-0.5 text-xs font-medium rounded-md flex items-center space-x-1"
                style={{
                  background: 'rgba(6, 182, 212, 0.15)',
                  color: '#22d3ee',
                  border: '1px solid rgba(6, 182, 212, 0.3)',
                }}
              >
                <i className="fa-solid fa-star text-[10px]"></i>
                <span>Promoted</span>
              </span>
            )}
          </div>
        </div>

        {/* Filename with format icon */}
        <div className="flex items-start space-x-2 mb-2">
          <span className="text-lg leading-none mt-0.5">{formatIcon}</span>
          <h3
            className="text-sm font-bold line-clamp-2 group-hover:text-blue-400 transition-colors"
            style={{ color: '#e2e8f0' }}
          >
            {artifact.filename}
          </h3>
        </div>

        {/* Artifact type */}
        <p className="text-xs mb-3" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
          {ARTIFACT_TYPE_LABELS[artifact.artifact_type] || artifact.artifact_type}
          <span className="mx-1.5">&middot;</span>
          .{artifact.file_format}
        </p>

        {/* Footer: QC status + file size + date */}
        <div
          className="flex items-center justify-between pt-3"
          style={{ borderTop: '1px solid rgba(100, 180, 255, 0.1)' }}
        >
          <div className="flex items-center space-x-2">
            {qcColor && qcLabel && (
              <span
                className="px-2 py-0.5 text-xs font-medium rounded-full"
                style={{
                  background: `${qcColor}22`,
                  color: qcColor,
                  border: `1px solid ${qcColor}44`,
                }}
              >
                {qcLabel}
              </span>
            )}
            {artifact.file_size_bytes && (
              <span className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                {formatFileSize(artifact.file_size_bytes)}
              </span>
            )}
          </div>
          <span className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
            {new Date(artifact.created_at).toLocaleDateString()}
          </span>
        </div>
      </div>
    </Link>
  )
}
